import { Field } from "rc-field-form";
import { Rule } from "rc-field-form/es/interface";
import * as React from "react";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import { KuikaEditorTitle } from "../../ui-kit/kuika-page-header/kuika-editor-title";

/**
 * The bar's name when the name is also an editable field: the connection's identity, a
 * pencil, and — while editing — the input with apply and discard.
 *
 * Passed to `KuikaFormPage`'s `renderTitle`, so it draws inside the form element. Outside it
 * the `name` field would bind to nothing and a rename would be silently dropped, which is
 * what the first version of the shell did.
 */
export interface IConnectionRenameTitleProps {
  title: string;
  /** What the connection IS while it is not being renamed: the switcher chip. */
  identity: React.ReactNode;
  isEditing: boolean;
  /** A discard needs a name to fall back to, so it is hidden until one exists. */
  canDiscard: boolean;
  onBeginEdit: () => void;
  onSubmit: () => void;
  onDiscard: () => void;
  placeholder: string;
  rules: Rule[];
  renameTestId?: string;
  inputTestId?: string;
  submitTestId?: string;
}

export const ConnectionRenameTitle = ({
  title,
  identity,
  isEditing,
  canDiscard,
  onBeginEdit,
  onSubmit,
  onDiscard,
  placeholder,
  rules,
  renameTestId,
  inputTestId,
  submitTestId
}: IConnectionRenameTitleProps): React.ReactElement => {
  // The field sits inside the form page's `<form>`: Enter there would also submit it — the
  // save — and Escape would reach whatever listens above the bar. Both stop at the field, the
  // same keys the code editors' and the table's name fields answer to.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      onSubmit();
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      if (canDiscard) onDiscard();
    }
  };

  // Renaming usually replaces the name outright, so the whole of it starts selected.
  const handleFocus = (event: React.FocusEvent<HTMLInputElement>): void => {
    event.currentTarget.select();
  };

  return (
    <KuikaEditorTitle
      className="kuika-connection-form__name"
      entityLabel="connection"
      identity={identity}
      isRenaming={isEditing}
      canAbortRename={canDiscard}
      onStartRename={onBeginEdit}
      onConfirmRename={onSubmit}
      onAbortRename={onDiscard}
      editTestId={renameTestId}
      confirmTestId={submitTestId}
      renameField={
        <Field name="name" rules={rules}>
          <KuikaInput
            size="sm"
            autoFocus
            placeholder={placeholder}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            data-testid={inputTestId}
          />
        </Field>
      }
    />
  );
};
